import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Button, Space} from 'antd';
import {StyledTableFilters, StyledTableFiltersSelect, StyledTableFiltersSearch} from './StyledTableFilters';
import AddExpenseButton from '../../../AddExpenseButton';
import ExportModal from '../../../../TransactionsPage/ExportModal';
import {fileHelpers, formHelpers, tableHelpers} from '../../../../../../utils/helpers';
import {
  DEFAULT_MODAL_PROPS,
  expensePaymentMethodConstants,
} from '../../../../../../constants';
import {expenseActions} from '../../../../../../state/actions/expense.actions';

const statuses = {
  REQUESTED: 'requested',
  REVIEWED: 'reviewed',
}

const selectProps = {
  allowClear: true,
  size: 'large',
  showSearch: true,
  styles: { popup: { root: {minWidth: 235} } },
  optionFilterProp: 'optionLabel',
  optionLabelProp: 'optionLabel'
}

const TableFilters = ({
  defaultQuery = {},
  filterFields = ['search'],
  exportAdditionalFields = {},
  onFilter,
}) => {
  const [t] = useTranslation(['main', 'expenses']);
  const [filter, setFilter] = useState({
    review_status: undefined,
    payment_method: undefined,
    policy_status: undefined,
    search: '',
    user_email: undefined,
  });
  const [exportModalProps, setExportModalProps] = useState(DEFAULT_MODAL_PROPS);

  const {employees, employeeEmail} = useSelector(({ user: { employee }, company: { employees } }) => ({
    employees,
    employeeEmail: employee?.email ?? null,
  }));

  const dispatch = useDispatch();

  useEffect(() => {
    if (!defaultQuery || Object.keys(defaultQuery).length === 0) return;
    setFilter({...filter, ...defaultQuery});
  }, [defaultQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const employeeOptions = useMemo(() =>
    formHelpers.getEmployeeOptions({employees, employeeEmail, t, valueKey: 'email'}), [employees, employeeEmail, t]);

  const paymentMethodOptions = [
    {label: t('paymentMethods.card'), value: expensePaymentMethodConstants.CARD},
    {label: t('paymentMethods.cash'), value: expensePaymentMethodConstants.CASH},
    {label: t('paymentMethods.wire'), value: expensePaymentMethodConstants.WIRE},
  ];

  const statusOptions = [
    {label: t('expenses:expensesList.viewOptions.requested'), value: statuses.REQUESTED},
    {label: t('expenses:expensesList.viewOptions.reviewed'), value: statuses.REVIEWED},
  ];

  const isEnabledField = (key) => filterFields.includes(key);

  const handleOnFilter = (key, value) => {
    const updatedFilter = {...filter, [key]: value};
    setFilter(updatedFilter);
    onFilter?.(updatedFilter);
  }

  const handleCancelExportModal = () => setExportModalProps({...exportModalProps, open: false, loading: false, error: null});

  const handleOpenExportModal = () => setExportModalProps({...exportModalProps, open: true});

  const handleOkExportModal = (data) => {

    const query = tableHelpers.formatFilterQuery({
      filter,
      data,
      exportAdditionalFields
    });

    if (!query) return;

    setExportModalProps({...exportModalProps, loading: true});

    dispatch(expenseActions.exportExpenses({
      query,
      successCallback: (response) => {
        fileHelpers.handleCSVExport(response, tableHelpers.getExportFileName({baseName: 'expenses', data}));
        handleCancelExportModal();
      },
      errorCallback: (response) => {
        setExportModalProps({
          ...exportModalProps,
          error: response?.response?.data?.message || t('validation.exportError'),
          loading: false
        });
      }
    }));
  }

  const renderSelect = ({key, placeholder, options, ...rest}) => isEnabledField(key) && (
    <StyledTableFiltersSelect
      allowClear
      onChange={(value) => handleOnFilter(key, value)}
      options={options}
      placeholder={placeholder}
      size='large'
      value={filter[key]}
      {...rest}
    />
  );

  return (
    <StyledTableFilters align='center'>
      <Space align='center' size='small'>
        {isEnabledField('search') && (
          <StyledTableFiltersSearch
            allowClear
            value={filter.search}
            onSearch={(value) => handleOnFilter('search', value)}
            placeholder={t('search')}
            size='large'
          />
        )}
        {renderSelect({
          key: 'payment_method',
          placeholder: t('paymentMethod'),
          options: paymentMethodOptions,
        })}
        {renderSelect({
          ...selectProps,
          key: 'user_email',
          placeholder: t('user'),
          options: employeeOptions,
        })}
        {renderSelect({
          key: 'review_status',
          placeholder: t('allStatuses'),
          options: statusOptions,
        })}
      </Space>

      <Space size='small'>
        <Button
          onClick={handleOpenExportModal}
          size='large'
        >
          {t('export')}
        </Button>
        <AddExpenseButton size='large'>+ {t('expense')}</AddExpenseButton>
      </Space>

      <ExportModal
        {...exportModalProps}
        options={['csv']}
        enableFormatField={false}
        handleCancel={handleCancelExportModal}
        handleOk={handleOkExportModal}
        title={t('export')}
      />

    </StyledTableFilters>
  );
}

TableFilters.propTypes = {
  exportAdditionalFields: PropTypes.object,
  onFilter: PropTypes.func,
  searchParams: PropTypes.object,
}

export default TableFilters;
